import React from "react";
import type { UseFormReturn } from "react-hook-form";

import { Checkbox } from "@/components/ui/checkbox";
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import RequiredMark from "@/components/ui/required-mark";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";

/**
 * Type for grouped material options returned from the API
 */
type EnclosureMaterialGroup = {
  group: string;
  options: Array<{
    id: number;
    material: string;
    type: string;
  }>;
};

/**
 * Props for the StandardEnclosureForm component
 */
interface StandardEnclosureFormProps {
  /**
   * React Hook Form instance for managing form state
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  form: UseFormReturn<any>;
  /**
   * Current repair type (tee, straight_line, or long_radius)
   */
  repairType: string | undefined;
  /**
   * Grouped enclosure shell options from the API
   */
  enclosureShellOptions?: EnclosureMaterialGroup[];
  /**
   * Grouped endplate options from the API
   */
  endplateOptions?: EnclosureMaterialGroup[];
  /**
   * Grouped ear options from the API
   */
  earOptions?: EnclosureMaterialGroup[];
  /**
   * Grouped stud bolt options from the API
   */
  studBoltOptions?: Array<{
    group: string;
    options: Array<{
      id: number;
      material: string;
      type: string;
    }>;
  }>;
  /**
   * Whether to show the riser material field (for tee type)
   */
  showRiserMaterial: boolean;
  /**
   * Callback when riser material checkbox changes
   */
  onRiserMaterialChange: (checked: boolean) => void;
}

/**
 * StandardEnclosureForm Component
 *
 * Renders the form fields for standard enclosure types (tee, straight_line, long_radius).
 * Includes:
 * - Enclosure Shell(s) / Header Shell selection (grouped)
 * - Endplates selection (grouped)
 * - Ears selection (grouped)
 * - Stud Bolts selection (grouped)
 * - Riser Shell selection (for tee type only, conditionally shown)
 * - Checkbox for matching header and riser materials (tee type only)
 *
 * @example
 * ```tsx
 * <StandardEnclosureForm
 *   form={form}
 *   repairType="tee"
 *   enclosureShellOptions={data.enclosureShellOptions}
 *   endplateOptions={data.endplateOptions}
 *   earOptions={data.earOptions}
 *   studBoltOptions={data.studBoltOptions}
 *   showRiserMaterial={false}
 *   onRiserMaterialChange={(checked) => setShowRiserMaterial(checked)}
 * />
 * ```
 */
export function StandardEnclosureForm({
  form,
  repairType,
  enclosureShellOptions,
  endplateOptions,
  earOptions,
  studBoltOptions,
  showRiserMaterial,
  onRiserMaterialChange,
}: StandardEnclosureFormProps) {
  return (
    <div className="flex justify-between w-full gap-11">
      <div className="flex flex-col flex-1 ">
        <FormField
          control={form.control}
          name="shells"
          render={({ field }) => (
            <FormItem>
              <FormLabel>
                {repairType === "tee" ? "Header Shell" : "Enclosure Shell(s)"}
                <RequiredMark />
              </FormLabel>
              <FormControl>
                <Select value={field.value || undefined} onValueChange={field.onChange}>
                  <SelectTrigger className={cn(!field.value && "[&>span]:text-gray-400")}>
                    <SelectValue placeholder="Select enclosure shell type" />
                  </SelectTrigger>
                  <SelectContent>
                    {enclosureShellOptions?.map((group, groupIndex) => (
                      <React.Fragment key={group.group}>
                        {groupIndex > 0 && <SelectSeparator />}
                        <SelectGroup>
                          <SelectLabel>{group.group}</SelectLabel>
                          {group.options.map((option) => (
                            <SelectItem key={option.id} value={String(option.id)}>
                              {option.material} - {option.type}
                            </SelectItem>
                          ))}
                        </SelectGroup>
                      </React.Fragment>
                    ))}
                  </SelectContent>
                </Select>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="endplates"
          render={({ field }) => (
            <FormItem>
              <FormLabel>
                Endplates
                <RequiredMark />
              </FormLabel>
              <FormControl>
                <Select value={field.value || undefined} onValueChange={field.onChange}>
                  <SelectTrigger className={cn(!field.value && "[&>span]:text-gray-400")}>
                    <SelectValue placeholder="Select endplate type" />
                  </SelectTrigger>
                  <SelectContent>
                    {endplateOptions?.map((group, groupIndex) => (
                      <React.Fragment key={group.group}>
                        {groupIndex > 0 && <SelectSeparator />}
                        <SelectGroup>
                          <SelectLabel>{group.group}</SelectLabel>
                          {group.options.map((option) => (
                            <SelectItem key={option.id} value={String(option.id)}>
                              {option.material} - {option.type}
                            </SelectItem>
                          ))}
                        </SelectGroup>
                      </React.Fragment>
                    ))}
                  </SelectContent>
                </Select>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="ears"
          render={({ field }) => (
            <FormItem>
              <FormLabel>
                Ears
                <RequiredMark />
              </FormLabel>
              <FormControl>
                <Select value={field.value || undefined} onValueChange={field.onChange}>
                  <SelectTrigger className={cn(!field.value && "[&>span]:text-gray-400")}>
                    <SelectValue placeholder="Select ear type" />
                  </SelectTrigger>
                  <SelectContent>
                    {earOptions?.map((group, groupIndex) => (
                      <React.Fragment key={group.group}>
                        {groupIndex > 0 && <SelectSeparator />}
                        <SelectGroup>
                          <SelectLabel>{group.group}</SelectLabel>
                          {group.options.map((option) => (
                            <SelectItem key={option.id} value={String(option.id)}>
                              {option.material} - {option.type}
                            </SelectItem>
                          ))}
                        </SelectGroup>
                      </React.Fragment>
                    ))}
                  </SelectContent>
                </Select>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="stud_bolts"
          render={({ field }) => (
            <FormItem>
              <FormLabel>
                Stud Bolts
                <RequiredMark />
              </FormLabel>
              <FormControl>
                <Select value={field.value || undefined} onValueChange={field.onChange}>
                  <SelectTrigger className={cn(!field.value && "[&>span]:text-gray-400")}>
                    <SelectValue placeholder="Select stud bolt type" />
                  </SelectTrigger>
                  <SelectContent>
                    {studBoltOptions?.map((group, groupIndex) => (
                      <React.Fragment key={group.group}>
                        {groupIndex > 0 && <SelectSeparator />}
                        <SelectGroup>
                          <SelectLabel>{group.group}</SelectLabel>
                          {group.options.map((option) => {
                            return (
                              <SelectItem key={option.id} value={String(option.id)}>
                                {option.material} - {option.type}
                              </SelectItem>
                            );
                          })}
                        </SelectGroup>
                      </React.Fragment>
                    ))}
                  </SelectContent>
                </Select>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {/* Special case for tees: show riser shell checkbox and field */}
        {repairType === "tee" && (
          <div className="flex items-center gap-2 mt-4">
            <Checkbox
              checked={showRiserMaterial}
              onChange={onRiserMaterialChange}
              label="Check if Header and Riser Material are same"
            />
          </div>
        )}

        {/* Show riser shell field if checkbox is NOT checked for tees */}
        {repairType === "tee" && !showRiserMaterial && (
          <FormField
            control={form.control}
            name="riser_shells"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  Riser Shell
                  <RequiredMark />
                </FormLabel>
                <FormControl>
                  <Select value={field.value || undefined} onValueChange={field.onChange}>
                    <SelectTrigger className={cn(!field.value && "[&>span]:text-gray-400")}>
                      <SelectValue placeholder="Select riser shell type" />
                    </SelectTrigger>
                    <SelectContent>
                      {enclosureShellOptions?.map((group, groupIndex) => (
                        <React.Fragment key={group.group}>
                          {groupIndex > 0 && <SelectSeparator />}
                          <SelectGroup>
                            <SelectLabel>{group.group}</SelectLabel>
                            {group.options.map((option) => (
                              <SelectItem key={option.id} value={String(option.id)}>
                                {option.material} - {option.type}
                              </SelectItem>
                            ))}
                          </SelectGroup>
                        </React.Fragment>
                      ))}
                    </SelectContent>
                  </Select>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        )}
      </div>
    </div>
  );
}
